import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FileText, Plus, Loader2 } from 'lucide-react';
import { getPrescriptionStatus, getOpenReimbursements, getPrescriptionHistory } from '../../../Services/APIs/reimbursement';
import PrescriptionForm from './PrescriptionForm';
import ClaimForm from './ClaimForm';
import ReuploadForm from './ReuploadForm';
import ReimbursementHistory from './ReimbursementHistory';
import ReimbursementCard from './ReimbursementCard';
import { DialogHeader, PRIMARY_BUTTON } from './FormShell';
import { reimbursementCards, proofLanding, TONE } from './reimbursementStatus';
import { getPhonepeMembers, memberByUid, memberLabel } from '../phonepeSession';

// The Reimbursement modal: a card per open Reimbursement (plus the latest closed one), starting a
// new one, past ones, and the forms. `initialView` opens it at one of the dashboard's Reimbursement
// actions: 'request' starts a new Reimbursement Prescription; 'proof' goes to the one Reimbursement
// awaiting proof of treatment, or to the list with a hint saying why there is none (proofLanding).
// Without it, the list.
const ReimbursementSection = ({ onClose, initialView }) => {
    const [openReimbursements, setOpenReimbursements] = useState([]);
    const [latest, setLatest] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    // { mode: 'request' } | { mode: 'upload_proof' | 'reupload', id, snapshot }. The form reads the
    // Reimbursement by id from the latest load, so a reload (e.g. coverage changed) reaches it.
    const [view, setView] = useState(() => (initialView === 'request' ? { mode: 'request' } : null));
    const [hint, setHint] = useState('');
    // Land on proof of treatment once, after the first successful load — not again on later refetches.
    const landOnProof = useRef(initialView === 'proof');
    const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
    const [members] = useState(getPhonepeMembers);

    const fetchStatus = useCallback(async () => {
        setIsLoading(true);
        setError('');
        try {
            const [openResponse, latestResponse] = await Promise.all([getOpenReimbursements(), getPrescriptionStatus()]);
            const records = openResponse.data?.data?.records;
            setOpenReimbursements(Array.isArray(records) ? records : []);
            const data = latestResponse.data?.data;
            setLatest(data && typeof data === 'object' && data._id ? data : null);
        } catch (err) {
            if (err?.response?.status !== 401) setError('Failed to load your reimbursements. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);

    const cards = reimbursementCards(openReimbursements, latest);

    useEffect(() => {
        if (!landOnProof.current || isLoading || error) return;
        landOnProof.current = false;
        const landing = proofLanding(reimbursementCards(openReimbursements, latest));
        if (landing.open) setView({ mode: 'upload_proof', id: landing.open._id, snapshot: landing.open });
        else setHint(landing.hint);
    }, [isLoading, error, openReimbursements, latest]);

    const handleFormDone = () => {
        setView(null);
        fetchStatus();
        setHistoryRefreshKey((k) => k + 1);
    };

    const handleAction = (action, reimbursement) => {
        setHint('');
        setView({ mode: action, id: reimbursement._id, snapshot: reimbursement });
    };
    const startNew = () => {
        setHint('');
        setView({ mode: 'request' });
    };
    const closeForm = () => setView(null);

    if (view?.mode === 'request') {
        return <PrescriptionForm onClose={closeForm} onSuccess={handleFormDone} />;
    }
    const current = view?.id ? (cards.find((r) => r._id === view.id) || view.snapshot) : null;
    if (view?.mode === 'upload_proof' && current) {
        return <ClaimForm reimbursement={current} onClose={closeForm} onSuccess={handleFormDone} onCoverageChanged={fetchStatus} />;
    }
    if (view?.mode === 'reupload' && current?.document_request) {
        return <ReuploadForm reimbursement={current} onClose={closeForm} onSuccess={handleFormDone} />;
    }

    const refreshing = isLoading && cards.length > 0;

    return (
        <>
            <div className="flex-shrink-0 border-b border-portal-border">
                <DialogHeader title="Reimbursement" subtitle="Treated outside the network? Get approved, then reimbursed." onClose={onClose}>
                    {refreshing && (
                        <p role="status" className="inline-flex items-center gap-1.5 text-xs font-semibold text-portal-purple-dark mt-1.5" data-testid="reimbursement-refreshing">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> Updating…
                        </p>
                    )}
                </DialogHeader>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain bg-portal-bg px-4 sm:px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]" data-testid="reimbursement-section">
                {error && (
                    <p role="alert" className="text-red-700 text-sm bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
                        {error} <button type="button" className="underline font-semibold" onClick={fetchStatus}>Retry</button>
                    </p>
                )}

                {hint && (
                    <p role="status" data-testid="reimbursement-hint" className={`text-xs border rounded-xl p-3 mb-4 ${TONE.review}`}>
                        {hint}
                    </p>
                )}

                {isLoading && cards.length === 0 && (
                    <div className="bg-white border border-portal-border rounded-2xl p-4 mb-4 animate-pulse" aria-label="Loading your reimbursements">
                        <div className="h-4 bg-portal-bg rounded w-1/2 mb-2" />
                        <div className="h-3 bg-portal-bg rounded w-3/4 mb-4" />
                        <div className="h-16 bg-portal-bg rounded-xl" />
                    </div>
                )}

                {!isLoading && !error && cards.length === 0 && (
                    <div className="bg-white border border-portal-border rounded-2xl p-4 mb-4" data-testid="reimbursement-empty">
                        <div className="flex items-center gap-2 mb-2">
                            <FileText className="w-4 h-4 text-portal-purple" aria-hidden="true" />
                            <p className="font-bold text-portal-ink text-sm">No reimbursement yet</p>
                        </div>
                        <ol className="text-xs text-portal-sub space-y-1.5 mb-4 list-decimal pl-4">
                            <li>Share your dentist's prescription — we review it before your treatment.</li>
                            <li>Once procedures are approved, get the treatment and upload proof of treatment.</li>
                            <li>We review it and reimburse you.</li>
                        </ol>
                        <button type="button" onClick={startNew} data-testid="next-step-new" className={`${PRIMARY_BUTTON} w-full`}>
                            Request reimbursement
                        </button>
                    </div>
                )}

                {cards.map((reimbursement) => (
                    <ReimbursementCard
                        key={reimbursement._id}
                        reimbursement={reimbursement}
                        patientLabel={memberLabel(memberByUid(members, reimbursement.uid))}
                        onAction={handleAction}
                    />
                ))}

                {cards.length > 0 && (
                    <button
                        type="button"
                        onClick={startNew}
                        data-testid="start-new-reimbursement"
                        className="w-full mb-5 flex items-center gap-3 text-left rounded-2xl border-2 border-dashed border-portal-purple-border bg-white hover:bg-portal-purple-light px-4 py-3 transition-colors"
                    >
                        <span className="w-9 h-9 rounded-full bg-portal-purple-light text-portal-purple flex items-center justify-center flex-shrink-0">
                            <Plus className="w-5 h-5" aria-hidden="true" />
                        </span>
                        <span className="min-w-0">
                            <span className="block text-sm font-semibold text-portal-purple-dark">Start a new reimbursement</span>
                            <span className="block text-xs text-portal-sub">For another treatment or another person on your policy.</span>
                        </span>
                    </button>
                )}

                <ReimbursementHistory
                    title="Past reimbursements"
                    apiFn={getPrescriptionHistory}
                    refreshKey={historyRefreshKey}
                    excludeIds={cards.map((r) => r._id)}
                    renderDetails={(r) => memberLabel(memberByUid(members, r.uid)) || r.clinic_name || '—'}
                    renderMeta={(r) => (memberByUid(members, r.uid) ? r.clinic_name : null)}
                />
            </div>
        </>
    );
};

export default ReimbursementSection;
